import React from "react";
import { COST_RATES } from "../../services/costRates";
import { formatZAR } from "../../services/plans";
import type { IntegrationKey } from "../../types";

interface Props {
  data: { integration: IntegrationKey; cents: number }[];
  size?: number;
  centerLabel?: string;
}

export function DonutChart({ data, size = 200, centerLabel }: Props) {
  const total = data.reduce((s, d) => s + d.cents, 0);
  const radius = size / 2;
  const inner = radius - 28;

  if (total === 0) {
    return <div className="empty-state" style={{ padding: 24 }}><h3>No spend</h3></div>;
  }

  let acc = 0;
  const arcs = data.map((d) => {
    const angle = (d.cents / total) * Math.PI * 2;
    const start = acc;
    acc += angle;
    return { ...d, start, end: acc };
  }).filter((a) => a.cents > 0);

  function arcPath(start: number, end: number): string {
    const cx = radius, cy = radius;
    const r = radius - 2, ri = inner;
    const large = end - start > Math.PI ? 1 : 0;
    const x1 = cx + Math.cos(start - Math.PI / 2) * r;
    const y1 = cy + Math.sin(start - Math.PI / 2) * r;
    const x2 = cx + Math.cos(end - Math.PI / 2) * r;
    const y2 = cy + Math.sin(end - Math.PI / 2) * r;
    const xi1 = cx + Math.cos(end - Math.PI / 2) * ri;
    const yi1 = cy + Math.sin(end - Math.PI / 2) * ri;
    const xi2 = cx + Math.cos(start - Math.PI / 2) * ri;
    const yi2 = cy + Math.sin(start - Math.PI / 2) * ri;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${xi1} ${yi1} A ${ri} ${ri} 0 ${large} 0 ${xi2} ${yi2} Z`;
  }

  return (
    <div className="row" style={{ gap: 24, alignItems: "center" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {arcs.map((a) => (
          <path
            key={a.integration}
            d={arcPath(a.start, a.end)}
            fill={COST_RATES[a.integration].color}
            opacity={0.92}
          >
            <title>{`${COST_RATES[a.integration].label}: ${formatZAR(a.cents)}`}</title>
          </path>
        ))}
        <text x={radius} y={radius - 4} textAnchor="middle" fontSize={18} fontWeight={800} fill="var(--text)">
          {formatZAR(total)}
        </text>
        <text x={radius} y={radius + 14} textAnchor="middle" fontSize={10} fill="var(--text-dim)">
          {centerLabel ?? "Total spend"}
        </text>
      </svg>

      <div className="col" style={{ gap: 8, flex: 1, minWidth: 0 }}>
        {data.map((d) => {
          const pct = total === 0 ? 0 : (d.cents / total) * 100;
          return (
            <div key={d.integration} className="row" style={{ gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: COST_RATES[d.integration].color, flexShrink: 0 }} />
              <span style={{ flex: 1, color: "var(--text-dim)", fontSize: 13 }}>{COST_RATES[d.integration].label}</span>
              <span className="tnum" style={{ color: "var(--text)", fontSize: 13, fontWeight: 600 }}>{formatZAR(d.cents)}</span>
              <span className="tnum muted" style={{ fontSize: 11.5, minWidth: 46, textAlign: "right" }}>{pct.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
